// marquee.js — 通用跑马灯（文字溢出时自动滚动）
//
// 用法：
//   import { createMarquee } from '../lib/marquee.js';
//   const m = createMarquee({ viewport, textEl, speed, pause });
//   m.setText('长文本...');
//   m.stop();    // 停止滚动
//   m.destroy(); // 彻底清理
//
// 滚动规则：文字向左移动，直到最后一个字符贴着视口右侧边缘时停止，
//          停留 pause 毫秒后回到初始位置，重新开始下一轮。
//
// 依赖 CSS（由调用方提供）：
//   .viewport.is-scrolling .text-el {
//     animation: marquee-scroll 24s linear forwards;
//   }
//   @keyframes marquee-scroll {
//     0%   { transform: translateX(0); }
//     100% { transform: translateX(var(--marquee-distance, 0)); }
//   }

/**
 * @param {Object} config
 * @param {HTMLElement} config.viewport - 视口容器（溢出隐藏，需有 .is-scrolling 的 CSS 规则）
 * @param {HTMLElement} config.textEl   - 文本元素（inline-block，需有动画的 CSS 规则）
 * @param {number} [config.speed]       - 滚动速度（像素/秒），默认 20
 * @param {number} [config.pause]       - 每轮滚动后的停留时间（毫秒），默认 3000
 * @returns {{ setText, stop, destroy }}
 */
export function createMarquee(config) {
  var viewport = config.viewport;
  var textEl = config.textEl;
  var SCROLL_SPEED = config.speed || 20;
  var PAUSE_DURATION = config.pause || 3000;

  var scrollTimer = null;
  var isScrollNeeded = false;

  // 单轮滚动结束：保持 forwards 末态停留 pause 毫秒 → 移除动画复位 → 再停留 pause 毫秒 → 重新滚动
  function onScrollEnd() {
    textEl.removeEventListener('animationend', onScrollEnd);
    // 末端停留：保留 .is-scrolling 让 forwards 保持末态
    scrollTimer = setTimeout(function() {
      viewport.classList.remove('is-scrolling');   // 移除动画，文字瞬切回初始位置
      void textEl.offsetWidth;                       // 强制重排，确保下次动画重新触发
      scrollTimer = setTimeout(startScroll, PAUSE_DURATION);
    }, PAUSE_DURATION);
  }

  // 启动单轮滚动
  function startScroll() {
    if (!isScrollNeeded) return;
    viewport.classList.add('is-scrolling');
    textEl.addEventListener('animationend', onScrollEnd);
  }

  // 停止循环
  function stop() {
    if (scrollTimer) { clearTimeout(scrollTimer); scrollTimer = null; }
    viewport.classList.remove('is-scrolling');
    textEl.removeEventListener('animationend', onScrollEnd);
    isScrollNeeded = false;
  }

  /**
   * 设置文本并自动检测是否需要滚动
   * @param {string} text
   */
  function setText(text) {
    textEl.textContent = text;
    stop();
    requestAnimationFrame(function() {
      var needScroll = textEl.scrollWidth > viewport.offsetWidth + 2;
      isScrollNeeded = needScroll;
      if (needScroll) {
        // 滚动距离 = 文本宽度 - 视口宽度（让最后一个字符贴视口右侧边缘时停止）
        var scrollDistance = textEl.scrollWidth - viewport.offsetWidth;
        textEl.style.setProperty('--marquee-distance', '-' + scrollDistance + 'px');
        textEl.style.animationDuration = (scrollDistance / SCROLL_SPEED) + 's';
        scrollTimer = setTimeout(startScroll, PAUSE_DURATION);
      }
    });
  }

  /**
   * 仅重新测量并启动滚动（不修改 textEl 的内容）。
   * 用于 textEl 内容已通过 innerHTML 等方式自行设置好的场景。
   */
  function recalc() {
    stop();
    requestAnimationFrame(function() {
      var needScroll = textEl.scrollWidth > viewport.offsetWidth + 2;
      isScrollNeeded = needScroll;
      if (needScroll) {
        var scrollDistance = textEl.scrollWidth - viewport.offsetWidth;
        textEl.style.setProperty('--marquee-distance', '-' + scrollDistance + 'px');
        textEl.style.animationDuration = (scrollDistance / SCROLL_SPEED) + 's';
        scrollTimer = setTimeout(startScroll, PAUSE_DURATION);
      }
    });
  }

  function destroy() {
    stop();
  }

  return { setText, stop, recalc, destroy };
}
